/**
 * 反馈状态管理 Hook (乐观更新)
 *
 * 提供评估反馈的状态管理，包含：
 * - 乐观更新机制
 * - 状态回滚和错误处理
 * - 积分奖励的即时反馈显示
 * - 重复提交防护
 *
 * @see Requirements 1.3, 2.3, 3.1, 3.2, 3.3
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import assessmentService from "@/services/assessmentService";
import { ApiError } from "@/types/apiErrors";

// ============ 类型定义 ============

/** 反馈状态 */
export interface FeedbackState {
  /** 当前状态 */
  status: "idle" | "submitting" | "success" | "error";
  /** 用户反馈类型 */
  userFeedback: "positive" | "negative" | null;
  /** 提交时间 */
  submittedAt?: string;
  /** 是否可以提交反馈 */
  canSubmit: boolean;
  /** 错误信息 */
  error?: string;
}

/** Hook 选项 */
export interface UseFeedbackStateOptions {
  /** 评估 ID */
  assessmentId: string | number;
  /** 初始状态 */
  initialState?: Partial<FeedbackState>;
  /** 成功回调 */
  onSuccess?: (
    feedback: "positive" | "negative",
    pointsEarned?: number,
  ) => void;
  /** 错误回调 */
  onError?: (error: Error) => void;
}

/** Hook 返回值 */
export interface UseFeedbackStateReturn {
  /** 当前反馈状态 */
  feedbackState: FeedbackState;
  /** 提交反馈 */
  submitFeedback: (feedback: "positive" | "negative") => Promise<void>;
  /** 是否正在加载初始状态 */
  isLoading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 重置错误状态 */
  clearError: () => void;
}

// ============ 默认状态 ============

const DEFAULT_FEEDBACK_STATE: FeedbackState = {
  status: "idle",
  userFeedback: null,
  canSubmit: true,
};

// ============ Hook 实现 ============

/**
 * 反馈状态管理 Hook
 *
 * 使用乐观更新策略提升用户体验：
 * 1. 立即更新UI状态
 * 2. 发送API请求
 * 3. 成功时显示积分奖励
 * 4. 失败时回滚状态并显示错误
 */
export function useFeedbackState(
  options: UseFeedbackStateOptions,
): UseFeedbackStateReturn {
  const { assessmentId, initialState, onSuccess, onError } = options;
  const { t } = useTranslation("assessments");

  // ============ 状态管理 ============

  const [feedbackState, setFeedbackState] = useState<FeedbackState>(() => ({
    ...DEFAULT_FEEDBACK_STATE,
    ...initialState,
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 使用 ref 跟踪是否正在提交，防止并发提交
  const isSubmittingRef = useRef(false);

  // ============ 初始化状态 ============

  useEffect(() => {
    // 如果提供了初始状态，直接使用
    if (initialState) {
      setFeedbackState((prev) => ({
        ...prev,
        ...initialState,
      }));
      return;
    }

    // 否则从评估详情中获取反馈状态
    const loadFeedbackState = async () => {
      if (!assessmentId) return;

      setIsLoading(true);
      setError(null);

      try {
        const assessment = await assessmentService.getAssessment(assessmentId);

        // 优先使用新的 feedback_status 字段（来自 AssessmentConversionSerializer）
        if (assessment.feedback_status) {
          const { user_feedback, feedback_submitted_at, can_submit_feedback } =
            assessment.feedback_status;

          setFeedbackState({
            status: user_feedback ? "success" : "idle",
            userFeedback: user_feedback,
            submittedAt: feedback_submitted_at || undefined,
            canSubmit: can_submit_feedback,
          });
        } else {
          // 向后兼容：使用旧的字段
          const hasSubmittedFeedback = assessment.user_feedback != null;

          setFeedbackState({
            status: hasSubmittedFeedback ? "success" : "idle",
            userFeedback: assessment.user_feedback || null,
            submittedAt: hasSubmittedFeedback
              ? assessment.completed_at || undefined
              : undefined,
            canSubmit: !hasSubmittedFeedback,
          });
        }
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.message
            : t("feedback.loadError", "加载反馈状态失败");

        setError(errorMessage);
        console.error("Failed to load feedback state:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFeedbackState();
  }, [assessmentId]); // 移除 initialState 和 t 避免无限循环

  // ============ 提交反馈 ============

  const submitFeedback = useCallback(
    async (feedback: "positive" | "negative") => {
      // 防护检查
      if (!assessmentId) {
        const errorMsg = t("feedback.noAssessmentId", "评估ID不存在");
        setError(errorMsg);
        onError?.(new Error(errorMsg));
        return;
      }

      // 防止并发提交
      if (isSubmittingRef.current) {
        return;
      }

      // 检查是否可以提交
      if (!feedbackState.canSubmit) {
        const errorMsg = t("feedback.alreadySubmitted", "您已经提交过反馈了");
        toast.info(errorMsg);
        return;
      }

      // 保存当前状态用于回滚
      const previousState = { ...feedbackState };

      // 标记正在提交
      isSubmittingRef.current = true;

      // 1. 乐观更新 - 立即更新UI
      setFeedbackState({
        status: "success",
        userFeedback: feedback,
        canSubmit: false,
        submittedAt: new Date().toISOString(),
      });

      setError(null);

      try {
        // 2. 发送API请求
        const response = await assessmentService.submitFeedback(
          assessmentId,
          feedback,
        );

        // 3. 显示积分奖励信息
        if (response.reward?.points && response.reward.points > 0) {
          toast.success(
            response.reward.message ||
              t("feedback.pointsEarned", "感谢反馈！获得 {{points}} 积分奖励", {
                points: response.reward.points,
              }),
          );
          onSuccess?.(feedback, response.reward.points);
        } else {
          toast.success(
            response.message || t("feedback.success", "反馈提交成功"),
          );
          onSuccess?.(feedback);
        }

        // 4. 更新最终状态（确保与服务器同步）
        setFeedbackState((prev) => ({
          ...prev,
          status: "success",
          userFeedback: feedback,
          canSubmit: false,
          submittedAt: new Date().toISOString(),
        }));
      } catch (err) {
        // 5. 失败回滚
        setFeedbackState({
          ...previousState,
          status: "error",
        });

        const errorMessage =
          err instanceof ApiError
            ? err.message
            : t("feedback.submitError", "提交反馈失败，请重试");

        setError(errorMessage);
        toast.error(errorMessage);
        onError?.(err as Error);

        console.error("Failed to submit feedback:", err);
      } finally {
        // 清除提交标记
        isSubmittingRef.current = false;
      }
    },
    [assessmentId, feedbackState, onSuccess, onError, t],
  );

  // ============ 清除错误 ============

  const clearError = useCallback(() => {
    setError(null);
    setFeedbackState((prev) => {
      if (prev.status === "error") {
        return {
          ...prev,
          status: "idle",
        };
      }
      return prev;
    });
  }, []);

  // ============ 返回值 ============

  return {
    feedbackState,
    submitFeedback,
    isLoading,
    error,
    clearError,
  };
}

export default useFeedbackState;
